import { motion } from "framer-motion"
import { BookMarked, Gift, Home, type LucideIcon, Search, Users } from "lucide-react"
import { NavLink } from "react-router-dom"
import { useHaptic } from "@/shared/hooks/use-haptic"
import { TRANSITION } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"

export function BottomNavigation() {
	const { triggerSelection } = useHaptic()

	return (
		<motion.nav
			layoutRoot
			aria-label="Основные разделы"
			data-tour="nav"
			className="app-bottom-nav fixed inset-x-2 z-40 mx-auto max-w-md rounded-[1.75rem] border border-white/10 bg-background/85 px-1 py-1.5 shadow-2xl shadow-black/40 backdrop-blur-xl"
		>
			<div className="grid grid-cols-5 items-center">
				<TabLink path="/" icon={Home} label="Главная" onSelect={triggerSelection} />
				<TabLink path="/library" icon={BookMarked} label="Библиотека" onSelect={triggerSelection} />

				<NavLink
					to="/search"
					onClick={triggerSelection}
					aria-label="Поиск"
					data-tour="search"
					className="group mx-auto -mt-7 flex flex-col items-center gap-1 rounded-3xl focus-visible:outline-none"
				>
					{({ isActive }) => (
						<>
							<motion.span
								animate={{ scale: isActive ? 1.06 : 1 }}
								transition={TRANSITION.spring}
								className="flex size-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/40 ring-4 ring-background transition-transform group-active:scale-95 group-focus-visible:ring-ring"
							>
								<Search className="size-6" strokeWidth={2.5} />
							</motion.span>
							<span
								className={cn(
									"text-[10px] font-medium max-[359px]:px-0 max-[359px]:text-[9px]",
									isActive ? "text-primary" : "text-muted-foreground",
								)}
							>
								Поиск
							</span>
						</>
					)}
				</NavLink>

				<TabLink path="/about" icon={Users} label="Сообщество" onSelect={triggerSelection} />
				<TabLink path="/referral" icon={Gift} label="Рефералы" onSelect={triggerSelection} />
			</div>
		</motion.nav>
	)
}

function TabLink({
	path,
	icon: Icon,
	label,
	onSelect,
}: {
	path: string
	icon: LucideIcon
	label: string
	onSelect: () => void
}) {
	return (
		<NavLink
			to={path}
			end={path === "/"}
			onClick={onSelect}
			className="relative flex min-h-14 flex-col items-center justify-center gap-1 rounded-2xl px-0.5 text-[10px] font-medium max-[359px]:px-0 max-[359px]:text-[9px]"
		>
			{({ isActive }) => (
				<>
					{isActive && (
						<motion.span
							layoutId="bottom-nav-active"
							className="absolute inset-0 rounded-2xl bg-secondary/70"
							transition={TRANSITION.spring}
						/>
					)}
					<motion.span
						className="relative flex w-full min-w-0 flex-col items-center gap-1"
						animate={{ scale: isActive ? 1.06 : 1 }}
						transition={TRANSITION.spring}
					>
						<Icon
							className={cn(
								"size-5 shrink-0 transition-colors",
								isActive ? "text-primary" : "text-muted-foreground",
							)}
						/>
						<span
							className={cn(
								"max-w-full truncate",
								isActive ? "text-primary" : "text-muted-foreground",
							)}
						>
							{label}
						</span>
					</motion.span>
				</>
			)}
		</NavLink>
	)
}
